import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Gem, Coins, Check } from 'lucide-react';
import type { CardDeck } from '@shared/schema';

interface FeaturedDeckProps {
  readonly deck: CardDeck;
  readonly onClick: () => void;
  readonly isOwned?: boolean;
}

export function FeaturedDeck({ deck, onClick, isOwned }: Readonly<FeaturedDeckProps>) {
  // Helper function to get card image URL
  const getCardImageUrl = (deckUrl: string, cardCode: string): string => {
    const imageUrl = `${deckUrl}/${cardCode}.svg`;
    return imageUrl;
  };
  
  return (
    <motion.div 
      className="cursor-pointer"
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}
    >
      <div className="relative overflow-hidden rounded-lg border border-green-600/30 bg-black/80">
        
        
        <div className="px-4 py-6 flex items-center gap-4">
          <div className="flex-grow">
            <h3 className="text-xl font-bold mb-2 text-white">{deck.name}</h3>
            
            <div className="flex items-center gap-2">
              <Badge className="bg-green-600 text-white font-medium border-0 text-xs px-2">
                {deck.membership === "free" ? "Free" : "Premium"}
              </Badge>
              <Badge variant="outline" className="border-gray-700 bg-black/40 text-white capitalize text-xs">
                {deck.deckType}
              </Badge>
              
              {isOwned && (
                <Badge className="bg-blue-600/80 text-white text-xs font-medium flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  <span>Owned</span>
                </Badge>
              )}
              
              {parseFloat(deck.price) > 0 && (
                <div className="text-amber-400 font-medium flex items-center text-sm">
                  {Math.floor(parseFloat(deck.price))}
                  {deck.currencyType === 'premium' ? (
                    <Gem className="h-3 w-3 ml-0.5 text-purple-400" />
                  ) : (
                    <Coins className="h-3 w-3 ml-0.5" />
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex-shrink-0">
            <div className="relative h-24 w-28">
              {/* Back card */}
              <div className="absolute inset-0 translate-x-2">
                <img
                  src={getCardImageUrl(deck.deckUrl, "back")}
                  alt="Card Back"
                  className="h-full w-auto object-contain"
                />
              </div>
              
              {/* Front card (King) */}
              <div className="absolute inset-0">
                <img
                  src={getCardImageUrl(deck.deckUrl, "B13")}
                  alt="King"
                  className="h-full w-auto object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = getCardImageUrl(deck.deckUrl, "back");
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}